// Comprehensive Tennessee family law data based on Custody X Change and official Tennessee sources
const tennesseeData = {
  'tennessee': {
    name: 'Tennessee',
    overview: {
      keyPoints: [
        'Tennessee follows the "best interests of the child" standard with specific factors outlined in Tennessee Code § 36-6-106',
        'Tennessee uses "parenting time" terminology instead of traditional visitation language',
        'The state has a strong preference for both parents to maintain meaningful relationships with their children',
        'Tennessee requires detailed parenting plans in all cases involving minor children',
        'Court-ordered mediation is mandatory in contested custody cases unless domestic violence is involved',
        'Tennessee has specific relocation restrictions requiring court approval for moves over 50 miles from the other parent',
        'The state presumes that frequent and continuing contact with both parents is in the child\'s best interest',
        'Tennessee has mandatory parent education requirements for all divorce and custody cases'
      ],
      uniqueFeatures: [
        'Tennessee uses "parenting time" instead of visitation, emphasizing both parents\' ongoing roles',
        'The state has a 50-mile relocation threshold that requires court approval',
        'Tennessee allows temporary parenting plans during pending cases',
        'The state has extensive alternative dispute resolution requirements before trial',
        'Tennessee recognizes both traditional and non-traditional family structures',
        'The state has specialized family court procedures with dedicated family court judges',
        'Tennessee requires mandatory parent education classes called "Children First"',
        'The state allows for collaborative divorce and mediation-based resolutions'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Tennessee for at least 6 months before filing, or Tennessee must be the child\'s home state under UCCJEA',
      filing: 'File in the county where either parent resides or where the child has lived for the past 6 months',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption',
      documentation: 'Required documents include petition, summons, parenting plan, child support worksheets, and financial affidavit',
      uniqueRules: [
        'Tennessee requires financial disclosure in all family law cases involving children',
        'Mandatory mediation required before contested custody hearings (exceptions for domestic violence)',
        'Guardian ad litem appointment is discretionary but common in contested cases',
        'Parent education classes ("Children First") required within 60 days of filing',
        'Tennessee allows for temporary parenting plans to be entered quickly for immediate arrangements'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Tennessee uses "residential schedule" to describe where and when the child spends time with each parent',
      legalCustody: 'Called "decision-making responsibility" - involves authority for major decisions about the child\'s welfare',
      jointCustody: 'Tennessee presumes joint decision-making is in the child\'s best interest unless evidence shows otherwise',
      visitation: 'Referred to as "parenting time" - detailed schedules for when the child is with each parent',
      specialProvisions: [
        'Supervised parenting time may be ordered when child safety is a concern',
        'Long-distance parenting schedules accommodate parents living more than 50 miles apart',
        'Extended summer parenting time is common for non-primary residential parents',
        'Holiday parenting time follows specific statutory guidelines with alternating arrangements',
        'Virtual parenting time (video calls, phone contact) is encouraged and protected',
        'Grandparent visitation rights are recognized under specific circumstances'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed parenting time schedule including regular rotation, weekends, holidays, and school breaks',
        'Decision-making responsibilities for education, healthcare, religious training, and extracurricular activities',
        'Methods for resolving disputes between parents including mediation requirements',
        'Transportation arrangements and responsibility for costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during parenting time',
        'Relocation provisions and notification requirements for moves affecting parenting time schedules',
        'Any restrictions, conditions, or special accommodations for either parent',
        'Provisions for changes to the parenting plan as children mature',
        'Guidelines for introducing new partners to children',
        'Emergency contact procedures and decision-making protocols'
      ],
      tips: [
        'Tennessee courts favor detailed, specific parenting plans that minimize future disputes',
        'Include provisions for Tennessee\'s varying weather conditions affecting travel',
        'Address how to handle missed parenting time due to illness, weather, or emergencies',
        'Specify meeting locations that are convenient and safe for exchanges',
        'Include provisions for the child\'s participation in activities and school events',
        'Address how major decisions will be made and communicated between parents',
        'Plan for Tennessee\'s unique geography and potential travel distances',
        'Include technology use guidelines for communication during parenting time'
      ]
    },
    childSupport: {
      guidelines: 'Tennessee uses a modified Income Shares Model based on both parents\' gross income with Tennessee-specific guidelines',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Parenting time with each parent (significant time can reduce support)',
        'Special needs of the children requiring additional expenses'
      ],
      calculation: 'Tennessee Child Support Guidelines using Income Shares Model with state-specific support schedule',
      modifications: 'Substantial change in circumstances required, typically 15% change in support amount or significant change in income or parenting time',
      enforcement: 'Strong enforcement through Tennessee Department of Human Services including wage garnishment, tax intercept, license suspension, and asset seizure',
      detailedInfo: {
        basicCalculation: {
          description: 'Tennessee uses a modified Income Shares Model based on the principle that children should receive the same proportion of parental income they would have received if the family remained intact.',
          steps: [
            'Calculate each parent\'s gross monthly income from all sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate adjusted gross monthly income for each parent after allowable deductions',
            'Add both parents\' adjusted gross incomes to determine combined monthly income',
            'Use Tennessee Child Support Schedule to find basic support obligation',
            'Allocate support proportionally based on each parent\'s income percentage',
            'Add costs for health insurance, childcare, and extraordinary expenses',
            'Apply any adjustments for extended parenting time or special circumstances',
            'Calculate final monthly support amount'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, etc.)',
            'Foster care payments for non-biological children',
            'Child support received for other children',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or domestic partner',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Reimbursement for job-related expenses'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'State income taxes (Tennessee has limited state income tax)',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        parentingTimeAdjustment: {
          description: 'Tennessee allows adjustment to child support when the paying parent has significant parenting time with the child.',
          thresholds: [
            'No adjustment for less than 25% parenting time (91 nights per year)',
            'Potential adjustment for 25-35% parenting time (91-127 nights per year)',
            'Standard adjustment for 35%+ parenting time (128+ nights per year)',
            'Equal parenting time may result in no support or offset calculations'
          ],
          calculation: 'Adjustment considers additional direct costs during parenting time and reduced costs to primary parent',
          factors: [
            'Actual overnight parenting time over previous 12 months',
            'Additional housing costs for accommodating child during parenting time',
            'Duplicate expenses (clothing, toys, school supplies) in both homes',
            'Transportation costs for parenting time exchanges',
            'Reduced household expenses for primary parent during parenting time periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically for covering the child',
            allocation: 'Added to basic support amount - paying parent responsible for reasonable cost',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage',
              'Insurance must be available through employer or Tennessee marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance information and cards'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs for employment, education, or job training',
            allocation: 'Added to basic support amount - paying parent responsible for proportional share',
            requirements: 'Must be work-related or education-related necessity',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for local area and type of care',
              'Before and after school care typically included',
              'Summer childcare during school breaks may be included',
              'Documentation and receipts required'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance',
              'Orthodontic treatment and appliances',
              'Vision care including glasses and contact lenses',
              'Mental health counseling and therapy',
              'Physical therapy and rehabilitation',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies',
              'Treatment for chronic conditions',
              'Emergency medical treatment',
              'Specialized medical consultations'
            ],
            allocation: 'Shared proportionally based on parents\' income percentages',
            procedure: 'Parent should seek advance approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment',
            examples: [
              'Youth sports teams, equipment, and travel',
              'Music lessons and instrument rental',
              'Dance, martial arts, or gymnastics classes',
              'Summer camps and day programs',
              'School trips and educational activities',
              'Tutoring and educational support',
              'Art, theater, or other creative programs'
            ],
            note: 'Not automatically included - must be agreed upon or court-ordered',
            considerations: [
              'Activities should benefit the child and be age-appropriate',
              'Costs should be reasonable and within family means',
              'Both parents should have input on major activity decisions'
            ]
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 15% or more)',
            'Significant change in parenting time schedule',
            'Change in health insurance costs or availability',
            'Change in childcare expenses',
            'Child\'s needs change significantly',
            'Emancipation or change in number of children',
            'More than 3 years since last order review'
          ],
          process: [
            'File Petition for Modification of Child Support',
            'Serve other parent with petition and supporting documents',
            'Provide updated financial affidavits and worksheets',
            'Attend required mediation if ordered',
            'Present case at court hearing',
            'Obtain modified support order'
          ],
          timeline: 'Modifications effective from date of filing petition, not final order date',
          documentation: [
            'Updated Financial Affidavit',
            'Current Child Support Worksheets',
            'Recent pay stubs and tax returns',
            'Documentation of changed circumstances',
            'Current childcare and insurance costs'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and wage garnishment',
            'Tax refund intercept (state and federal)',
            'Asset seizure and bank account levies',
            'License suspension (driver\'s, professional, recreational)',
            'Credit bureau reporting',
            'Contempt of court proceedings',
            'Property liens and asset seizure',
            'Passport denial for large arrearages',
            'Lottery and gaming winnings intercept'
          ],
          interest: 'Unpaid support accrues interest at 12% per annum on past-due amounts',
          statute: 'No statute of limitations on collecting past-due child support',
          resources: [
            'Tennessee Department of Human Services Child Support Division',
            'Local district attorney offices',
            'Private collection agencies contracted by state',
            'Interstate enforcement agencies'
          ]
        }
      }
    },
    mediation: {
      description: 'Tennessee requires mediation in most contested custody cases',
      requirements: [
        'Mandatory before trial in contested custody cases',
        'Both parties must attend and participate in good faith',
        'Mediator must be court-approved or agreed upon by parties',
        'Confidential process - statements cannot be used in court'
      ],
      benefits: [
        'Less expensive than going to trial',
        'Faster resolution of disputes',
        'Parents maintain more control over outcomes',
        'Less adversarial than court proceedings',
        'Can preserve co-parenting relationships'
      ],
      process: [
        'Court orders mediation or parties agree to mediate',
        'Select qualified mediator experienced in family law',
        'Attend mediation session with or without attorneys',
        'Work toward agreement on custody and parenting time',
        'Any agreement reached is put in writing and submitted to court'
      ]
    },
    settlementProcess: {
      description: 'Tennessee encourages settlement through a structured 3-step process',
      steps: [
        {
          step: 1,
          title: 'Preparation and Information Gathering',
          details: [
            'Complete all required financial disclosures',
            'Gather documentation of parenting involvement',
            'Attend mandatory parent education classes',
            'Consider hiring attorney or mediator',
            'Identify key issues and priorities'
          ]
        },
        {
          step: 2,
          title: 'Negotiation and Mediation',
          details: [
            'Participate in good faith mediation',
            'Explore various parenting time arrangements',
            'Discuss child support calculations',
            'Address decision-making responsibilities',
            'Work on communication protocols'
          ]
        },
        {
          step: 3,
          title: 'Finalization and Court Approval',
          details: [
            'Draft comprehensive parenting plan',
            'Submit agreement to court for approval',
            'Attend final hearing if required',
            'Ensure all legal requirements are met',
            'Begin implementation of new arrangements'
          ]
        }
      ]
    },
    resources: {
      courts: [
        {
          name: 'Tennessee Administrative Office of the Courts',
          url: 'http://www.tncourts.gov/',
          description: 'Official Tennessee court system website with forms, procedures, and self-help resources'
        },
        {
          name: 'Tennessee Family Court Forms',
          url: 'http://www.tncourts.gov/courts/circuit-courts/forms',
          description: 'Complete collection of Tennessee family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Davidson County (Nashville) Family Court',
          address: '408 Second Avenue North, Nashville, TN 37201',
          phone: '(615) 862-5601',
          description: 'Family court serving Nashville and Davidson County'
        },
        {
          name: 'Shelby County (Memphis) Family Court',
          address: '140 Adams Avenue, Memphis, TN 38103',
          phone: '(901) 222-3000',
          description: 'Family court serving Memphis and Shelby County'
        },
        {
          name: 'Knox County (Knoxville) Family Court',
          address: '400 Main Street, Knoxville, TN 37902',
          phone: '(865) 215-2379',
          description: 'Family court serving Knoxville and Knox County'
        },
        {
          name: 'Hamilton County (Chattanooga) Family Court',
          address: '625 Georgia Avenue, Chattanooga, TN 37402',
          phone: '(423) 209-6700',
          description: 'Family court serving Chattanooga and Hamilton County'
        }
      ],
      support: [
        {
          name: 'Tennessee Department of Human Services - Child Support Services',
          url: 'https://www.tn.gov/humanservices/for-families/child-support-services.html',
          phone: '1-800-838-6911',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Legal Aid Society of Middle Tennessee',
          url: 'https://las.org/',
          phone: '(615) 244-6610',
          description: 'Free legal services for low-income families in family law matters'
        },
        {
          name: 'Tennessee Bar Association Family Law Section',
          url: 'https://www.tba.org/sections/family-law',
          description: 'Professional resources and attorney referrals for family law'
        },
        {
          name: 'Tennessee Coalition to End Domestic and Sexual Violence',
          url: 'https://tncoalition.org/',
          phone: '1-800-289-9018',
          description: 'Statewide domestic violence resources and legal advocacy'
        },
        {
          name: 'Tennessee Fathers Rights Groups',
          url: 'https://www.facebook.com/groups/tennesseefathersrights',
          description: 'Support networks and advocacy for fathers navigating family court in Tennessee'
        }
      ],
      legal: [
        {
          name: 'Tennessee Bar Association Lawyer Referral Service',
          url: 'https://www.tba.org/for-the-public/find-a-lawyer',
          phone: '(615) 383-7421',
          description: 'Professional attorney referrals for family law cases throughout Tennessee'
        },
        {
          name: 'Nashville School of Law Legal Clinic',
          url: 'https://nashvilleschooloflaw.edu/legal-clinic/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'University of Memphis Cecil C. Humphreys School of Law Legal Clinic',
          url: 'https://www.memphis.edu/law/academics/clinics/',
          description: 'Clinical legal services including family law representation'
        },
        {
          name: 'Tennessee Collaborative Law Group',
          url: 'https://www.collaborativelaw.org/find-a-professional/tennessee/',
          description: 'Network of collaborative divorce professionals in Tennessee'
        },
        {
          name: 'Tennessee Mediation Network',
          url: 'https://www.tnmediation.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        }
      ],
      educational: [
        {
          name: 'Children First Tennessee',
          url: 'http://www.tncourts.gov/programs/children-first',
          description: 'Mandatory parent education program for divorcing parents with minor children'
        },
        {
          name: 'Tennessee Parenting Plan Resources',
          url: 'http://www.tncourts.gov/courts/circuit-courts/parenting-plans',
          description: 'Comprehensive guide to creating parenting plans in Tennessee'
        },
        {
          name: 'Tennessee Guardian ad Litem Program',
          url: 'http://www.tncourts.gov/programs/guardian-ad-litem',
          description: 'Information about guardian ad litem services for children'
        },
        {
          name: 'University of Tennessee Extension - Family Life',
          url: 'https://extension.tennessee.edu/programs/family/',
          description: 'Educational programs for co-parenting and family relationships'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Davidson County Circuit Court',
            address: '408 Second Avenue North, Nashville, TN 37201',
            phone: '(615) 862-5601',
            jurisdiction: 'Davidson County - Nashville metropolitan area'
          },
          {
            name: 'Shelby County Circuit Court',
            address: '140 Adams Avenue, Memphis, TN 38103',
            phone: '(901) 222-3000',
            jurisdiction: 'Shelby County - Memphis metropolitan area'
          },
          {
            name: 'Knox County Circuit Court',
            address: '400 Main Street, Knoxville, TN 37902',
            phone: '(865) 215-2379',
            jurisdiction: 'Knox County - Knoxville metropolitan area'
          },
          {
            name: 'Hamilton County Circuit Court',
            address: '625 Georgia Avenue, Chattanooga, TN 37402',
            phone: '(423) 209-6700',
            jurisdiction: 'Hamilton County - Chattanooga metropolitan area'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$184.50',
          'Divorce (without children)': '$184.50',
          'Custody/Parenting Plan': '$184.50',
          'Child Support': '$184.50',
          'Modification': '$184.50',
          'Paternity': '$184.50',
          'Protection Order': 'No fee'
        },
        requiredForms: [
          'Complaint for Divorce or Petition for Custody',
          'Summons',
          'Parenting Plan',
          'Child Support Worksheets',
          'Financial Affidavit',
          'Certificate of Service'
        ]
      },
      statutes: [
        {
          name: 'Tennessee Code Title 36 - Domestic Relations',
          url: 'https://law.justia.com/codes/tennessee/2019/title-36/',
          description: 'Primary statutes governing divorce, custody, and family law in Tennessee'
        },
        {
          name: 'Tennessee Child Support Guidelines (T.C.A. § 36-5-101)',
          url: 'https://law.justia.com/codes/tennessee/2019/title-36/chapter-5/',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Tennessee Parental Relocation Statute (T.C.A. § 36-6-108)',
          url: 'https://law.justia.com/codes/tennessee/2019/title-36/chapter-6/section-36-6-108/',
          description: 'Laws governing parent relocation with children'
        }
      ],
      emergencyContacts: [
        {
          name: 'Tennessee Domestic Violence Hotline',
          phone: '1-800-289-9018',
          description: '24/7 statewide domestic violence crisis line'
        },
        {
          name: 'Tennessee Child Abuse Hotline',
          phone: '1-877-237-0004',
          description: '24/7 child abuse and neglect reporting'
        },
        {
          name: 'Crisis Text Line',
          phone: 'Text HOME to 741741',
          description: '24/7 crisis support via text message'
        }
      ]
    },
    bestInterestsFactors: [
      'The love, affection, and emotional ties existing between the parents and child',
      'The disposition of the parents to provide the child with food, clothing, medical care, education and other necessary care and the degree to which a parent has been the primary caregiver',
      'The importance of continuity in the child\'s life and the length of time the child has lived in a stable, satisfactory environment',
      'The stability of the family unit of the parents and the relative strength of the child\'s bond with one or both of the prospective custodians',
      'The preference of the child if twelve (12) years of age or older',
      'Evidence of physical or emotional abuse to the child, to the other parent or to any other person',
      'The character and behavior of any other person who resides in or frequents the home of a parent',
      'The reasonable preference of the child, if twelve (12) years of age or older',
      'Each parent\'s past and potential for future performance of parenting responsibilities',
      'The willingness and ability of each of the parents to facilitate and encourage a close and continuing parent-child relationship between the child and both of the child\'s parents',
      'The disposition of each parent to provide the child with food, clothing, medical care, education and other necessary care',
      'The degree to which a parent has been the primary caregiver'
    ],
    practicalTips: [
      'Tennessee courts strongly favor detailed, specific parenting plans over general arrangements',
      'Complete the mandatory "Children First" parent education class as early as possible',
      'Document all interactions and communications with the other parent using email or text',
      'Be prepared for mandatory mediation - Tennessee courts require it in most contested cases',
      'Keep detailed records of your parenting time and any expenses related to the children',
      'Follow all court orders precisely - violations can seriously impact future custody decisions',
      'Tennessee judges appreciate parents who demonstrate flexibility and child-focus',
      'Consider hiring a local attorney familiar with Tennessee family court procedures'
    ]
  }
};

export default tennesseeData;